import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { offerPublicApi } from '../lib/offerApi';
import PublicVendorDetail from './PublicVendorDetail';
import {
  ChevronDown, ChevronUp, Check, AlertCircle, Download,
  ArrowRight, ArrowLeft, Lock, TrendingDown, ExternalLink, Eye
} from 'lucide-react';

const CATEGORY_META = {
  food: { icon: '🍽', label: 'Food & Meals', color: 'bg-orange-50 border-orange-200 text-orange-700' },
  transport: { icon: '🚗', label: 'Transport', color: 'bg-blue-50 border-blue-200 text-blue-700' },
  health: { icon: '🏥', label: 'Health & Wellness', color: 'bg-emerald-50 border-emerald-200 text-emerald-700' },
  insurance: { icon: '🛡', label: 'Insurance', color: 'bg-purple-50 border-purple-200 text-purple-700' },
  housing: { icon: '🏠', label: 'Real Estate & Housing', color: 'bg-amber-50 border-amber-200 text-amber-700' },
  education: { icon: '📚', label: 'Education & Training', color: 'bg-indigo-50 border-indigo-200 text-indigo-700' },
  devices: { icon: '💻', label: 'Devices & Work Tools', color: 'bg-slate-50 border-slate-200 text-slate-700' },
  lifestyle: { icon: '🎉', label: 'Lifestyle & Entertainment', color: 'bg-pink-50 border-pink-200 text-pink-700' },
};

const NTA_BANDS = [
  { limit: 800000, rate: 0.00 }, { limit: 2200000, rate: 0.15 }, { limit: 3200000, rate: 0.18 },
  { limit: 3200000, rate: 0.21 }, { limit: 3200000, rate: 0.24 }, { limit: Infinity, rate: 0.24 },
];

function clientCalcTax(grossMonthly, monthlyBenefits) {
  const g = grossMonthly, b = monthlyBenefits;
  const aG = g * 12, aB = b * 12, aT = aG - aB;
  const eP = Math.round(aT * 0.08), erP = Math.round(aG * 0.10);
  const cra = Math.max(200000, aG * 0.01) + aG * 0.20;
  const ci = Math.max(0, aT - eP - cra);
  let r = ci, ap = 0;
  for (const { limit, rate } of NTA_BANDS) { if (r <= 0) break; const t = Math.min(r, limit); ap += Math.round(t * rate); r -= t; }
  const mP = Math.round(ap / 12), mEP = Math.round(eP / 12), mErP = Math.round(erP / 12);
  const th = g - b - mEP - mP;
  // Without
  const nbP = Math.round(aG * 0.08), nbCra = Math.max(200000, aG * 0.01) + aG * 0.20;
  const nbCi = Math.max(0, aG - nbP - nbCra);
  let nbR = nbCi, nbAp = 0;
  for (const { limit, rate } of NTA_BANDS) { if (nbR <= 0) break; const t = Math.min(nbR, limit); nbAp += Math.round(t * rate); nbR -= t; }
  const nbMp = Math.round(nbAp / 12), nbMeP = Math.round(nbP / 12), nbTh = g - nbMeP - nbMp;
  return { paye: mP, pension: mEP, erPension: mErP, takeHome: th, totalValue: th + b, totalWithPension: th + b + mErP, nbPaye: nbMp, nbPension: nbMeP, nbTakeHome: nbTh, taxSaved: nbMp - mP };
}

const fmt = (n) => `₦${Number(n || 0).toLocaleString()}`;
const fmtAnnual = (n) => `₦${Number((n || 0) * 12).toLocaleString()}`;

export default function CandidateOfferPage() {
  const { token } = useParams();
  const [offer, setOffer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [view, setView] = useState('overview'); // overview, allocator, confirm, accepted
  const [selections, setSelections] = useState({});
  const [expandedCat, setExpandedCat] = useState(null);
  const [viewingVendor, setViewingVendor] = useState(null);
  const [signedName, setSignedName] = useState('');
  const [agreed, setAgreed] = useState({ terms: false, benefits: false, tax: false });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    loadOffer();
  }, [token]);

  const loadOffer = async () => {
    try {
      const data = await offerPublicApi.get(token);
      if (data.detail) { setError(data.detail); return; }
      setOffer(data);
      // Init selections
      if (data.candidate_selections) {
        setSelections(data.candidate_selections);
        if (data.status === 'accepted') setView('accepted');
        else if (data.status === 'awaiting_acceptance') setView('confirm');
      } else {
        // Pre-fill with minimums
        const init = {};
        (data.enabled_categories || []).forEach(cat => {
          init[cat] = (data.min_allocations || {})[cat] || 0;
        });
        setSelections(init);
      }
    } catch (err) {
      setError('Failed to load offer. The link may be expired or invalid.');
    } finally {
      setLoading(false);
    }
  };

  const totalAllocated = useMemo(() => Object.values(selections).reduce((s, v) => s + v, 0), [selections]);
  const remaining = offer ? offer.benefits_budget - totalAllocated : 0;
  const tax = useMemo(() => offer ? clientCalcTax(offer.gross_monthly, totalAllocated) : null, [offer, totalAllocated]);

  const updateAllocation = useCallback((cat, amount) => {
    const min = (offer?.min_allocations || {})[cat] || 0;
    const val = Math.max(min, Math.min(amount, offer.benefits_budget));
    setSelections(prev => ({ ...prev, [cat]: val }));
  }, [offer]);

  const handleConfirmSelections = async () => {
    if (remaining !== 0) return;
    setSubmitting(true);
    try {
      const data = await offerPublicApi.selectBenefits(token, selections);
      if (data.detail) { alert(data.detail); setSubmitting(false); return; }
      setOffer(data);
      setView('confirm');
    } catch (err) {
      alert('Failed to submit selections');
    } finally {
      setSubmitting(false);
    }
  };

  const handleAccept = async () => {
    if (!agreed.terms || !agreed.benefits || !agreed.tax || !signedName.trim()) return;
    setSubmitting(true);
    try {
      const data = await offerPublicApi.accept(token, {
        signed_name: signedName.trim(),
        agreed_terms: agreed.terms,
        agreed_benefits: agreed.benefits,
        agreed_tax: agreed.tax,
      });
      if (data.detail) { alert(data.detail); setSubmitting(false); return; }
      setOffer(data);
      setView('accepted');
    } catch (err) {
      alert('Failed to accept offer');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <LoadingScreen />;
  if (error) return <ErrorScreen message={error} />;
  if (!offer) return <ErrorScreen message="Offer not found" />;

  const firstName = offer.candidate_name?.split(' ')[0] || 'there';

  return (
    <div className="min-h-screen bg-[#FBF9F7]" data-testid="candidate-offer-page">
      {/* Header */}
      <header className="bg-white border-b border-stone-100 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 h-14 flex items-center">
          <img src="/pebbles_logo.svg" alt="Pebbles" className="h-7" />
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8 space-y-8">
        {viewingVendor ? (
          <PublicVendorDetail vendorId={viewingVendor} onBack={() => setViewingVendor(null)} />
        ) : (
          <>
            {view === 'overview' && <OverviewSection offer={offer} tax={tax} firstName={firstName} onNext={() => setView('allocator')} />}
            {view === 'allocator' && (
              <AllocatorSection
                offer={offer} selections={selections} totalAllocated={totalAllocated}
                remaining={remaining} tax={tax} expandedCat={expandedCat}
                setExpandedCat={setExpandedCat} updateAllocation={updateAllocation}
                onBack={() => setView('overview')} onConfirm={handleConfirmSelections}
                submitting={submitting} onViewVendor={setViewingVendor}
              />
            )}
            {view === 'confirm' && (
          <ConfirmSection
            offer={offer} selections={selections} tax={tax}
            signedName={signedName} setSignedName={setSignedName}
            agreed={agreed} setAgreed={setAgreed}
            onBack={() => setView('allocator')} onAccept={handleAccept}
            submitting={submitting}
          />
        )}
        {view === 'accepted' && <AcceptedSection offer={offer} firstName={firstName} />}
          </>
        )}
      </div>

      {/* Compliance Footer */}
      <footer className="border-t border-stone-200 bg-white mt-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 text-center">
          <p className="text-[10px] text-[#636E72] leading-relaxed max-w-2xl mx-auto">
            All benefits are fully compliant with Nigerian tax law (NTA 2025). Company-provided benefits delivered through approved vendors are tax-exempt under the Personal Income Tax Act. Pebbles ensures full regulatory compliance.
          </p>
          <p className="text-xs text-stone-400 mt-3">Powered by Pebbles | pebbles.financial</p>
        </div>
      </footer>
    </div>
  );
}

// ============================================================
// B.1 OVERVIEW SECTION
// ============================================================
function OverviewSection({ offer, tax, firstName, onNext }) {
  const companyName = offer.company?.name || 'Your new employer';
  return (
    <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500" data-testid="overview-section">
      {/* Welcome */}
      <div>
        <h1 className="text-3xl sm:text-4xl font-light text-[#2D3436] font-[Manrope] tracking-tight">
          Hello {firstName},
        </h1>
        <div className="mt-4 space-y-3 text-[#636E72]">
          <p>Congratulations! <strong className="text-[#2D3436]">{companyName}</strong> is excited to offer you the position of <strong className="text-[#2D3436]">{offer.position_title}</strong>.</p>
          <p>Before you sign, we want to show you exactly what your compensation looks like — not just your salary, but the full picture including benefits, tax impact, and access to Nigeria's best vendors at preferred rates.</p>
        </div>
      </div>

      {/* Total Compensation */}
      <div className="bg-white rounded-2xl border border-stone-100 shadow-sm p-6 sm:p-8">
        <h2 className="text-xs font-semibold text-[#636E72] uppercase tracking-widest mb-5">Your Total Compensation Package</h2>
        <p className="text-4xl sm:text-5xl font-extrabold text-[#2D3436] font-[Manrope] tracking-tight">
          {fmt(offer.gross_monthly + offer.benefits_budget)}
        </p>
        <p className="text-sm text-[#636E72] mt-1">Total Monthly Package Value</p>
        <div className="mt-6 space-y-3 bg-stone-50 rounded-xl p-5">
          <div className="flex justify-between text-sm"><span className="text-[#636E72]">Gross Monthly Salary</span><span className="font-semibold">{fmt(offer.gross_monthly)}</span></div>
          <div className="flex justify-between text-sm"><span className="text-[#636E72]">+ Company-Provided Benefits</span><span className="font-semibold text-[#D64D7A]">+ {fmt(offer.benefits_budget)}</span></div>
          <div className="flex justify-between text-sm"><span className="text-[#636E72]">+ Employer Pension Contribution</span><span className="font-semibold">+ {fmt(tax?.erPension)}</span></div>
          <div className="flex justify-between text-sm pt-3 border-t border-stone-200"><span className="font-bold text-[#2D3436]">Total Company Investment in YOU</span><span className="font-extrabold text-[#2D3436] font-[Manrope]">{fmt(offer.gross_monthly + offer.benefits_budget + (tax?.erPension || 0))}/month</span></div>
        </div>
      </div>

      {/* Why This Matters */}
      <div className="space-y-3">
        <h2 className="text-lg font-bold text-[#2D3436] font-[Manrope]">Here's Why This Matters</h2>
        <p className="text-sm text-[#636E72]">Most companies hand you a salary and leave you to figure out the rest. {companyName} does things differently.</p>
        <p className="text-sm text-[#636E72]">Your company provides <strong className="text-[#2D3436]">{fmt(offer.benefits_budget)}/month</strong> in benefits through approved vendors — food, transport, health, and more. Because these are company-provided benefits (not salary), they are tax-exempt under Nigerian law.</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4">
          {[
            'You pay LESS tax on your income',
            'You get MORE total value from the same package',
            'You access premium vendors at group rates',
            'Every naira of your benefits is spent on things you actually need',
          ].map((t, i) => (
            <div key={i} className="flex items-start gap-2.5 bg-white rounded-xl p-3 border border-stone-100">
              <Check className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-[#2D3436]">{t}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Tax Comparison */}
      <div className="space-y-3">
        <h2 className="text-lg font-bold text-[#2D3436] font-[Manrope]">The Tax Difference</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-white rounded-2xl border border-stone-200 p-5">
            <p className="text-xs font-semibold text-[#636E72] uppercase tracking-wider mb-4">Without Benefits (salary only)</p>
            <div className="space-y-2 text-sm">
              <Row label="Gross" value={fmt(offer.gross_monthly)} />
              <Row label="Pension" value={`-${fmt(tax?.nbPension)}`} negative />
              <Row label="PAYE" value={`-${fmt(tax?.nbPaye)}`} negative />
              <div className="pt-2 border-t border-stone-200"><Row label="Take-home" value={fmt(tax?.nbTakeHome)} bold /></div>
              <Row label="Benefits" value="₦0" />
              <div className="pt-2 border-t border-stone-200"><Row label="TOTAL VALUE" value={fmt(tax?.nbTakeHome)} bold big /></div>
            </div>
          </div>
          <div className="bg-white rounded-2xl border-2 border-[#D64D7A]/30 p-5 relative">
            <div className="absolute -top-3 right-4 bg-emerald-500 text-white text-[10px] font-bold px-3 py-1 rounded-full uppercase tracking-wider">Recommended</div>
            <p className="text-xs font-semibold text-[#D64D7A] uppercase tracking-wider mb-4">With Your Benefits (Pebbles structure)</p>
            <div className="space-y-2 text-sm">
              <Row label="Gross" value={fmt(offer.gross_monthly)} />
              <Row label="Benefits" value={`-${fmt(offer.benefits_budget)}`} pink />
              <Row label="Pension" value={`-${fmt(tax?.pension)}`} negative />
              <Row label="PAYE" value={`-${fmt(tax?.paye)}`} negative />
              <div className="pt-2 border-t border-stone-200"><Row label="Take-home" value={fmt(tax?.takeHome)} bold /></div>
              <Row label="+ Benefits" value={`+${fmt(offer.benefits_budget)}`} pink />
              <Row label="+ Pension" value={`+${fmt(tax?.erPension)}`} muted />
              <div className="pt-2 border-t border-stone-200"><Row label="TOTAL VALUE" value={fmt(tax?.totalWithPension)} bold big /></div>
            </div>
            <div className="mt-4 bg-emerald-50 rounded-xl p-3 text-sm font-semibold text-emerald-700 text-center">
              You gain {fmt(tax?.taxSaved)}/month ({fmtAnnual(tax?.taxSaved)}/year)
            </div>
          </div>
        </div>
      </div>

      {/* Compliance Note */}
      <div className="bg-amber-50 rounded-xl p-4 border border-amber-200 flex items-start gap-3">
        <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
        <p className="text-xs text-amber-800 leading-relaxed">All benefits are fully compliant with Nigerian tax law (NTA 2025, FIRS guidelines). Company-provided benefits delivered through approved vendors are legitimately tax-exempt. This is not a loophole — it's how the tax code is designed to work.</p>
      </div>

      <button onClick={onNext} className="w-full py-4 bg-[#D64D7A] hover:bg-[#B52D5B] text-white rounded-2xl font-semibold text-base shadow-[0_4px_20px_rgba(214,77,122,0.3)] transition-all active:scale-[0.98] flex items-center justify-center gap-2" data-testid="choose-benefits-btn">
        Choose Your Benefits <ArrowRight className="w-5 h-5" />
      </button>
    </div>
  );
}

// ============================================================
// B.2 ALLOCATOR SECTION
// ============================================================
function AllocatorSection({ offer, selections, totalAllocated, remaining, tax, expandedCat, setExpandedCat, updateAllocation, onBack, onConfirm, submitting, onViewVendor }) {
  const pctAllocated = offer.benefits_budget > 0 ? Math.round(totalAllocated / offer.benefits_budget * 100) : 0;

  return (
    <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500" data-testid="allocator-section">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold text-[#2D3436] font-[Manrope] tracking-tight">Choose Your Benefits</h1>
        <p className="text-sm text-[#636E72] mt-2">You have <strong className="text-[#2D3436]">{fmt(offer.benefits_budget)}</strong>/month to allocate across benefit categories. Your tax impact updates in real-time.</p>
      </div>

      {/* Allocation Bar */}
      <div className="bg-white rounded-2xl border border-stone-100 shadow-sm p-5">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-semibold text-[#2D3436]">Remaining to allocate: <span className={remaining === 0 ? 'text-emerald-600' : 'text-[#D64D7A]'}>{fmt(remaining)}</span></span>
          <span className="text-xs text-[#636E72]">{pctAllocated}% allocated</span>
        </div>
        <div className="w-full h-3 bg-stone-100 rounded-full overflow-hidden">
          <div className={`h-full rounded-full transition-all duration-300 ${remaining === 0 ? 'bg-emerald-500' : 'bg-[#D64D7A]'}`} style={{ width: `${Math.min(pctAllocated, 100)}%` }} />
        </div>
      </div>

      {/* Category Allocators */}
      <div className="space-y-3">
        {(offer.enabled_categories || []).map(cat => {
          const meta = CATEGORY_META[cat] || { icon: '📦', label: cat, color: 'bg-stone-50 border-stone-200 text-stone-700' };
          const amount = selections[cat] || 0;
          const minAmount = (offer.min_allocations || {})[cat] || 0;
          const pct = offer.benefits_budget > 0 ? Math.round(amount / offer.benefits_budget * 100) : 0;
          const vendors = (offer.vendors_by_category || {})[cat] || [];
          const isExpanded = expandedCat === cat;

          return (
            <div key={cat} className="bg-white rounded-2xl border border-stone-100 shadow-sm overflow-hidden" data-testid={`category-${cat}`}>
              <div className="p-5">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <span className="text-xl">{meta.icon}</span>
                    <span className="font-semibold text-[#2D3436]">{meta.label}</span>
                    {minAmount > 0 && <span className="flex items-center gap-1 text-[10px] text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full"><Lock className="w-3 h-3" /> Min: {fmt(minAmount)}</span>}
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-[#636E72]">₦</span>
                    <input
                      type="number"
                      value={amount}
                      onChange={e => updateAllocation(cat, +e.target.value)}
                      min={minAmount}
                      step={1000}
                      className="w-28 h-9 rounded-lg border border-stone-200 px-3 text-sm font-bold text-right font-[Manrope] focus:ring-2 focus:ring-[#D64D7A]/20"
                      data-testid={`input-${cat}`}
                    />
                  </div>
                </div>
                <div className="w-full h-2 bg-stone-100 rounded-full overflow-hidden">
                  <div className="h-full bg-[#D64D7A] rounded-full transition-all duration-300" style={{ width: `${pct}%` }} />
                </div>
                <div className="flex items-center justify-between mt-2">
                  <p className="text-xs text-[#636E72]">{pct}% of budget</p>
                  {vendors.length > 0 && (
                    <button onClick={() => setExpandedCat(isExpanded ? null : cat)} className="text-xs text-[#D64D7A] font-medium flex items-center gap-1 hover:underline" data-testid={`toggle-vendors-${cat}`}>
                      See all {cat} vendors {isExpanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                    </button>
                  )}
                </div>
              </div>

              {/* Vendor Explorer */}
              {isExpanded && vendors.length > 0 && (
                <div className="border-t border-stone-100 bg-stone-50/50 p-5 space-y-3 animate-in fade-in duration-200" data-testid={`vendors-${cat}`}>
                  <p className="text-xs text-[#636E72]">Your {meta.label.toLowerCase()} allocation gives you access to all of these vendors. No sign-up fees, no separate accounts.</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                    {vendors.map(v => (
                      <button key={v.id} onClick={() => onViewVendor && onViewVendor(v.id)} className="bg-white rounded-xl p-3.5 border border-stone-100 hover:border-pink-200 hover:shadow-sm transition-all text-left group cursor-pointer" data-testid={`view-vendor-${v.id}`}>
                        <div className="flex items-center justify-between">
                          <p className="font-semibold text-sm text-[#2D3436]">{v.name}</p>
                          <Eye className="w-3.5 h-3.5 text-stone-300 group-hover:text-[#D64D7A] transition-colors" />
                        </div>
                        <p className="text-xs text-[#636E72] mt-1 line-clamp-2">{v.description}</p>
                        {v.metadata?.locations && <p className="text-[10px] text-[#636E72] mt-1.5 flex items-center gap-1">📍 {v.metadata.locations}</p>}
                        <p className="text-[10px] text-[#D64D7A] font-medium mt-1.5 opacity-0 group-hover:opacity-100 transition-opacity">View products & packages →</p>
                      </button>
                    ))}
                  </div>
                  <p className="text-xs text-[#636E72]">That's {vendors.length} {meta.label.toLowerCase()} vendors — all accessible with your allocation.</p>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Live Tax Impact */}
      <div className="bg-white rounded-2xl border border-stone-100 shadow-sm p-5 sm:p-6 sticky bottom-4" data-testid="live-tax-panel">
        <div className="flex items-center gap-2 mb-4">
          <TrendingDown className="w-4 h-4 text-[#D64D7A]" />
          <h3 className="text-sm font-bold text-[#2D3436]">Live Tax Impact</h3>
          <span className="text-[10px] bg-emerald-50 text-emerald-600 px-2 py-0.5 rounded-full font-medium">Updates as you adjust</span>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-center">
          <Stat label="Take-home Cash" value={fmt(tax?.takeHome)} />
          <Stat label="Benefits Value" value={fmt(totalAllocated)} pink />
          <Stat label="Monthly PAYE" value={fmt(tax?.paye)} />
          <Stat label="Tax Saved" value={fmt(tax?.taxSaved)} green />
        </div>
        <div className="mt-4 pt-4 border-t border-stone-100 flex items-center justify-between">
          <div>
            <p className="text-xs text-[#636E72]">Your Total Monthly Value</p>
            <p className="text-2xl font-extrabold text-[#2D3436] font-[Manrope]">{fmt(tax?.totalValue)}</p>
          </div>
          <p className="text-xs text-emerald-600 font-semibold bg-emerald-50 px-3 py-1.5 rounded-full">
            {fmt(tax?.taxSaved)}/mo saved ({fmt((tax?.taxSaved || 0) * 12)}/yr)
          </p>
        </div>
      </div>

      {/* Navigation */}
      <div className="flex items-center justify-between">
        <button onClick={onBack} className="px-4 py-2.5 text-sm font-medium text-[#636E72] hover:bg-stone-100 rounded-xl flex items-center gap-2" data-testid="back-to-overview">
          <ArrowLeft className="w-4 h-4" /> Back to Overview
        </button>
        <button
          onClick={onConfirm}
          disabled={remaining !== 0 || submitting}
          className="px-6 py-3 bg-[#D64D7A] hover:bg-[#B52D5B] text-white rounded-xl font-semibold text-sm shadow-[0_2px_10px_rgba(214,77,122,0.3)] transition-all active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          data-testid="confirm-selections-btn"
        >
          {submitting ? 'Submitting...' : remaining !== 0 ? `Allocate remaining ${fmt(remaining)}` : 'Confirm My Selections'} <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

// ============================================================
// B.4 CONFIRM & ACCEPT
// ============================================================
function ConfirmSection({ offer, selections, tax, signedName, setSignedName, agreed, setAgreed, onBack, onAccept, submitting }) {
  const totalBenefits = Object.values(selections).reduce((s, v) => s + v, 0);
  return (
    <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500" data-testid="confirm-section">
      <h1 className="text-2xl font-bold text-[#2D3436] font-[Manrope]">Confirm Your Selections</h1>

      {/* Selected Benefits */}
      <div className="bg-white rounded-2xl border border-stone-100 shadow-sm p-6">
        <h2 className="text-xs font-semibold text-[#636E72] uppercase tracking-widest mb-4">Your Chosen Benefits ({fmt(totalBenefits)}/month)</h2>
        <div className="space-y-3">
          {Object.entries(selections).filter(([, v]) => v > 0).map(([cat, amount]) => {
            const meta = CATEGORY_META[cat] || { icon: '📦', label: cat };
            const vendors = (offer.vendors_by_category || {})[cat] || [];
            const vendorNames = vendors.slice(0, 3).map(v => v.name).join(', ');
            return (
              <div key={cat} className="flex items-center gap-3">
                <span className="text-lg">{meta.icon}</span>
                <div className="flex-1">
                  <div className="flex justify-between"><span className="text-sm font-medium text-[#2D3436]">{meta.label}</span><span className="text-sm font-bold text-[#2D3436]">{fmt(amount)}/month</span></div>
                  {vendorNames && <p className="text-xs text-[#636E72] mt-0.5">{vendorNames}{vendors.length > 3 ? ` + ${vendors.length - 3} more` : ''}</p>}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Final Breakdown */}
      <div className="bg-white rounded-2xl border border-stone-100 shadow-sm p-6">
        <h2 className="text-xs font-semibold text-[#636E72] uppercase tracking-widest mb-4">Your Final Compensation Breakdown</h2>
        <div className="space-y-2 text-sm">
          <div className="flex justify-between"><span className="text-[#636E72]">Monthly take-home cash</span><span className="font-semibold">{fmt(tax?.takeHome)}</span></div>
          <div className="flex justify-between"><span className="text-[#636E72]">Monthly benefits value</span><span className="font-semibold text-[#D64D7A]">{fmt(totalBenefits)}</span></div>
          <div className="flex justify-between"><span className="text-[#636E72]">Employer pension</span><span className="font-semibold">{fmt(tax?.erPension)}</span></div>
          <div className="flex justify-between pt-3 border-t border-stone-200 font-bold"><span>Total monthly value</span><span className="font-extrabold font-[Manrope]">{fmt(tax?.totalWithPension)}</span></div>
          <div className="flex justify-between"><span>Annual total value</span><span className="font-extrabold font-[Manrope]">{fmtAnnual(tax?.totalWithPension)}</span></div>
        </div>
        <div className="mt-4 grid grid-cols-2 gap-3">
          <div className="bg-stone-50 rounded-xl p-3 text-center">
            <p className="text-xs text-[#636E72]">Monthly PAYE</p>
            <p className="font-bold font-[Manrope]">{fmt(tax?.paye)}</p>
          </div>
          <div className="bg-emerald-50 rounded-xl p-3 text-center">
            <p className="text-xs text-emerald-600">Annual Tax Savings</p>
            <p className="font-bold text-emerald-700 font-[Manrope]">{fmtAnnual(tax?.taxSaved)}</p>
          </div>
        </div>
      </div>

      {/* Acceptance */}
      <div className="bg-white rounded-2xl border border-stone-100 shadow-sm p-6">
        <div className="space-y-3 mb-5">
          <p className="text-sm text-[#636E72]">By accepting this offer, you agree to the terms of employment at {offer.company?.name} as outlined in the full offer letter. Your benefits selections can be changed quarterly through the Pebbles platform.</p>
          {[
            { key: 'terms', label: 'I have read and agree to the Terms of Employment' },
            { key: 'benefits', label: 'I understand my benefits are company-provided and tax-exempt under Nigerian law' },
            { key: 'tax', label: 'I agree to the benefit allocations above' },
          ].map(c => (
            <label key={c.key} className="flex items-start gap-3 cursor-pointer">
              <input type="checkbox" checked={agreed[c.key]} onChange={e => setAgreed(prev => ({ ...prev, [c.key]: e.target.checked }))} className="w-4 h-4 mt-0.5 text-[#D64D7A] rounded" data-testid={`agree-${c.key}`} />
              <span className="text-sm text-[#2D3436]">{c.label}</span>
            </label>
          ))}
        </div>
        <div>
          <label className="block text-sm font-semibold text-[#2D3436] mb-2">Signature</label>
          <input type="text" value={signedName} onChange={e => setSignedName(e.target.value)} placeholder="Type your full name to sign" className="w-full h-14 rounded-xl border-2 border-stone-200 px-5 text-lg font-[Manrope] italic focus:border-[#D64D7A] focus:ring-2 focus:ring-[#D64D7A]/20 transition-all" data-testid="signature-input" />
        </div>
      </div>

      <div className="flex items-center justify-between">
        <button onClick={onBack} className="px-4 py-2.5 text-sm font-medium text-[#636E72] hover:bg-stone-100 rounded-xl flex items-center gap-2" data-testid="modify-selections-btn">
          <ArrowLeft className="w-4 h-4" /> Modify Selections
        </button>
        <button
          onClick={onAccept}
          disabled={!agreed.terms || !agreed.benefits || !agreed.tax || !signedName.trim() || submitting}
          className="px-8 py-3.5 bg-[#D64D7A] hover:bg-[#B52D5B] text-white rounded-xl font-semibold shadow-[0_4px_20px_rgba(214,77,122,0.3)] transition-all active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          data-testid="accept-offer-btn"
        >
          {submitting ? 'Accepting...' : 'Accept Offer'} <Check className="w-5 h-5" />
        </button>
      </div>
    </div>
  );
}

// ============================================================
// B.5 ACCEPTED
// ============================================================
function AcceptedSection({ offer, firstName }) {
  return (
    <div className="flex items-center justify-center min-h-[60vh] animate-in fade-in slide-in-from-bottom-4 duration-500" data-testid="accepted-section">
      <div className="bg-white rounded-2xl border border-stone-100 shadow-sm p-8 sm:p-12 text-center max-w-lg w-full">
        <div className="w-20 h-20 bg-emerald-50 rounded-full flex items-center justify-center mx-auto mb-6">
          <Check className="w-10 h-10 text-emerald-500" />
        </div>
        <h1 className="text-2xl font-bold text-[#2D3436] font-[Manrope]">
          Welcome to {offer.company?.name}, {firstName}!
        </h1>
        <p className="text-[#636E72] mt-3">Your offer has been accepted. Your personalised offer letter is ready.</p>

        <div className="mt-8 space-y-3">
          <p className="text-sm font-semibold text-[#2D3436]">What happens next:</p>
          <div className="text-left space-y-2.5 max-w-xs mx-auto">
            {[
              'Your HR team has been notified',
              'You\'ll receive a Pebbles account invitation',
              'Your benefits will be active from your start date',
              'Your chosen vendors will be ready to use',
            ].map((t, i) => (
              <div key={i} className="flex items-start gap-2.5">
                <span className="text-xs font-bold text-[#D64D7A] bg-pink-50 w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">{i + 1}</span>
                <p className="text-sm text-[#636E72]">{t}</p>
              </div>
            ))}
          </div>
          <p className="text-sm text-[#636E72] mt-4">Start date: <strong className="text-[#2D3436]">{offer.start_date ? new Date(offer.start_date + 'T00:00:00').toLocaleDateString('en-NG', { day: 'numeric', month: 'long', year: 'numeric' }) : '-'}</strong></p>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// HELPERS
// ============================================================
function Row({ label, value, bold, big, negative, pink, muted }) {
  return (
    <div className={`flex justify-between ${big ? 'text-base' : ''}`}>
      <span className={`${bold ? 'font-bold text-[#2D3436]' : 'text-[#636E72]'}`}>{label}</span>
      <span className={`${bold ? 'font-extrabold font-[Manrope]' : 'font-medium'} ${negative ? 'text-red-500' : pink ? 'text-[#D64D7A]' : muted ? 'text-[#636E72]' : 'text-[#2D3436]'}`}>{value}</span>
    </div>
  );
}

function Stat({ label, value, pink, green }) {
  return (
    <div>
      <p className="text-[10px] text-[#636E72] uppercase tracking-wider font-semibold">{label}</p>
      <p className={`text-lg font-extrabold font-[Manrope] mt-0.5 ${pink ? 'text-[#D64D7A]' : green ? 'text-emerald-600' : 'text-[#2D3436]'}`}>{value}</p>
    </div>
  );
}

function LoadingScreen() {
  return (
    <div className="min-h-screen bg-[#FBF9F7] flex items-center justify-center">
      <div className="text-center">
        <div className="w-10 h-10 border-3 border-[#D64D7A] border-t-transparent rounded-full animate-spin mx-auto" />
        <p className="text-sm text-[#636E72] mt-4">Loading your offer...</p>
      </div>
    </div>
  );
}

function ErrorScreen({ message }) {
  return (
    <div className="min-h-screen bg-[#FBF9F7] flex items-center justify-center">
      <div className="text-center max-w-md p-8">
        <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
        <h1 className="text-xl font-bold text-[#2D3436] font-[Manrope]">Oops!</h1>
        <p className="text-[#636E72] mt-2">{message}</p>
      </div>
    </div>
  );
}
